import React from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type LicenceCardScanVisualState = "scanning" | "success" | "partial";

/**
 * Same card + laser sweep as the mobile licence upload scanning overlay (App.tsx).
 * Reused for QA verification run so the motion language matches.
 */
export function LicenceCardScanAnimation({
  state,
  title,
  description,
  darkMode,
  className,
}: {
  state: LicenceCardScanVisualState;
  title: string;
  description: string;
  darkMode?: boolean;
  className?: string;
}) {
  const dark = !!darkMode;
  return (
    <div className={cn("text-center", className)}>
      <div
        className={cn(
          "mx-auto mb-4 relative h-32 w-[220px] rounded-2xl overflow-hidden border",
          state === "success"
            ? "border-emerald-300 bg-slate-950 shadow-[inset_0_0_30px_rgba(16,185,129,0.22)]"
            : state === "partial"
              ? "border-amber-300 bg-slate-950 shadow-[inset_0_0_30px_rgba(245,158,11,0.2)]"
              : "border-indigo-200 bg-slate-950 shadow-[inset_0_0_30px_rgba(99,102,241,0.2)]",
        )}
      >
        <div className="absolute left-3 right-3 top-4 bottom-4 rounded-xl border border-white/20" />
        <div className="absolute left-5 right-5 top-7 h-2 rounded bg-white/10" />
        <div className="absolute left-5 right-5 top-[52px] h-2 rounded bg-white/10" />
        <div className="absolute left-5 right-20 top-[77px] h-2 rounded bg-white/10" />
        <div
          className={cn(
            "absolute left-3 right-3 h-[3px] rounded-full blur-[0.2px] animate-[rv-card-scan_1.35s_ease-in-out_infinite]",
            state === "success"
              ? "bg-emerald-400 shadow-[0_0_16px_rgba(16,185,129,0.8)]"
              : state === "partial"
                ? "bg-amber-400 shadow-[0_0_16px_rgba(245,158,11,0.8)]"
                : "bg-indigo-400 shadow-[0_0_16px_rgba(99,102,241,0.85)]",
          )}
        />
        <div
          className={cn(
            "absolute inset-0",
            state === "success"
              ? "bg-[linear-gradient(to_bottom,rgba(16,185,129,0.08),transparent_35%,rgba(16,185,129,0.08))]"
              : state === "partial"
                ? "bg-[linear-gradient(to_bottom,rgba(245,158,11,0.07),transparent_35%,rgba(245,158,11,0.07))]"
                : "bg-[linear-gradient(to_bottom,rgba(99,102,241,0.08),transparent_35%,rgba(99,102,241,0.08))]",
          )}
        />
        <div
          className={cn(
            "absolute right-4 top-3 h-3 w-3 rounded-full",
            state === "success"
              ? "bg-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.85)]"
              : state === "partial"
                ? "bg-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.85)]"
                : "bg-indigo-300 shadow-[0_0_10px_rgba(129,140,248,0.9)]",
          )}
        />
      </div>
      <h4
        className={cn(
          "text-base font-semibold",
          state === "success"
            ? "text-emerald-700"
            : state === "partial"
              ? "text-amber-700"
              : dark
                ? "text-slate-100"
                : "text-slate-900",
        )}
      >
        {title}
      </h4>
      <p className={cn("text-sm mt-1", dark ? "text-slate-400" : "text-slate-500")}>{description}</p>
      <style>{`
        @keyframes rv-card-scan {
          0% { transform: translateY(18px); opacity: 0.7; }
          50% { transform: translateY(92px); opacity: 1; }
          100% { transform: translateY(18px); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
